import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';

// Functions
import isExact from '../../functions/is-exact'

// Hooks
import useDebounce from '../../hooks/debounce'

// Filter Type Processors
import filterTypeProcessors from './filter-type-processors'

import { ListingContext, decodeQueryString, encodeQueryString } from '.';

export const ListingProvider = ({ queryString: queryStringParam, children, defaultQuery = {}, externalParams = {}, availableFilters, initialFilterData }) => {
	const queryRef = useRef(decodeQueryString({availableFilters, queryString: queryStringParam, defaultQuery, externalParams}))
	const [query, setQueryRaw] = useState(queryRef.current);
	const [filterData, setFilterData] = useState(initialFilterData ?? {});

	const [filtersModalVisible, setShowFiltersModal] = useState(false);
	const [activeModalFilterKey, setActiveModalFilterKey] = useState(false);

	const inputQuery = useMemo(() => {
		return decodeQueryString({availableFilters, queryString: queryStringParam, defaultQuery, externalParams});
	}, [queryStringParam, externalParams]);
	const debouncedInputQuery = useDebounce(inputQuery, 100, inputQuery);

	const [outputData, setOutputData] = useState({ queryString: queryStringParam, externalParams });
	const debouncedOutputData = useDebounce(outputData, 200, outputData);

	const filters = useMemo(() => {
		const filters = [];

		for(const filterKey in filterData) {
			const filter = availableFilters[filterKey];
			if(filter?.filterProcessor) {
				const filterProcessor = filterTypeProcessors[filter.filterProcessor];
				if(filterProcessor) {
					filters.push(filterProcessor({ inputQuery, query, filterData, filter, key: filterKey }));
				}
			}
		}

		const activeFilters = filters.filter(f => f.active);

		const totalSelections = activeFilters.reduce((t, f) => t + f.activeOptions ? f.activeOptions.length : 1, 0);
		
		return {
			activeFilters,
			filters,
			totalSelections,
		}
	}, [query, inputQuery, filterData, availableFilters])

	const clearFilters = useCallback(() => {
		setQuery({
			...defaultQuery,
			...externalParams
		});
	}, [defaultQuery, externalParams]);

	const showFiltersModal = (filterKey = true) => {
		if(filterKey && filterKey !== true) {
			setActiveModalFilterKey(filterKey)
		}

		setShowFiltersModal(!!filterKey);
	}

	const setQuery = (newQuery) => {
		if(!isExact(newQuery, queryRef.current)){
			queryRef.current = newQuery;
			setQueryRaw(queryRef.current);
		}
	}

	useEffect(() => {
		if(!isExact(debouncedInputQuery, queryRef.current)){
			setQuery(debouncedInputQuery)
		}
	}, [debouncedInputQuery])

	useEffect(() => {
		const newData = {
			queryString: encodeQueryString({
				query: omit(query, Object.keys(externalParams)),
				defaultQuery: defaultQuery
			}),
			externalParams,
		};

		if(!isExact(newData, outputData)) {
			setOutputData(newData)
		}
	}, [query, externalParams, defaultQuery])

	const setFilter = (filterKey, filterValue) => {
		if(filterValue) {
			setQuery({...queryRef.current, [filterKey]: filterValue})
		}
		else {
			setQuery(omit(queryRef.current, [filterKey]));
		}
	}

	const paginate = (page) => {
		setFilter('page', page.toString());
	}

	return (
		<ListingContext.Provider value={{
			isListing: true,
			inputQuery: debouncedInputQuery,
			query,
			setQuery,
			outputData: debouncedOutputData,
			paginate,
			setFilter,
			filterData: filters,
			setFilterData,
			filtersModalVisible,
			showFiltersModal,
			activeModalFilterKey,
			setActiveModalFilterKey,
			clearFilters,
		}}>
			{children}
		</ListingContext.Provider>
	)
}

ListingProvider.propTypes = {
	children: PropTypes.node,
	defaultQuery: PropTypes.object,
	queryString: PropTypes.string,
	availableFilters: PropTypes.object,
	initialFilterData: PropTypes.object,
	filterType: PropTypes.string,
}